import { useEffect, useState } from "react";
import { Loader2, Download, FileSpreadsheet } from "lucide-react";
import StaffHeader from "@/components/StaffHeader";
import InventorySubNav from "@/components/inventory/InventorySubNav";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function downloadBlob(data, filename) {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default function Reports() {
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("all");
  const now = new Date();
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [year, setYear] = useState(String(now.getFullYear()));
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingStock, setExportingStock] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    api.get("/inventory/locations").then(({ data }) => setLocations(data));
  }, []);

  const handleView = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/reports/monthly", { params: { location_id: locationId, month, year } });
      setReports(data);
    } finally {
      setLoading(false);
    }
  };

  const handleExportMonthly = async () => {
    setExporting(true);
    try {
      const response = await api.get("/inventory/reports/monthly/export", { params: { location_id: locationId, month, year }, responseType: "blob" });
      downloadBlob(response.data, `medical_inventory_${year}_${String(month).padStart(2, "0")}.xlsx`);
    } catch {
      toast({ variant: "destructive", title: "Could not export report" });
    } finally {
      setExporting(false);
    }
  };

  const handleExportStockOnHand = async () => {
    setExportingStock(true);
    try {
      const response = await api.get("/inventory/reports/stock-on-hand/export", { responseType: "blob" });
      downloadBlob(response.data, "medical_inventory_stock_on_hand.xlsx");
    } catch {
      toast({ variant: "destructive", title: "Could not export stock on hand" });
    } finally {
      setExportingStock(false);
    }
  };

  return (
    <div className="min-h-screen bg-amtm-bg">
      <StaffHeader />
      <div className="max-w-5xl mx-auto px-4 py-6 pb-24">
        <h1 className="font-display text-3xl text-amtm-text mb-2" data-testid="reports-title">Monthly Sheet Report</h1>
        <InventorySubNav />

        <div className="flex flex-wrap gap-3 items-end mb-4">
          <div>
            <Label>Drawer</Label>
            <Select value={locationId} onValueChange={setLocationId}>
              <SelectTrigger className="w-44 mt-1.5" data-testid="reports-location-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Drawers</SelectItem>
                {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Month</Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-40 mt-1.5" data-testid="reports-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTH_NAMES.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Year</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-28 mt-1.5" data-testid="reports-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>{[now.getFullYear(), now.getFullYear() - 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={handleView} disabled={loading} data-testid="reports-view-button" variant="outline">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} View
          </Button>
          <Button onClick={handleExportMonthly} disabled={exporting} data-testid="reports-export-monthly-button" className="bg-amtm-teal text-white hover:bg-amtm-tealHover">
            {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Export to Excel
          </Button>
        </div>

        <div className="mb-6">
          <Button onClick={handleExportStockOnHand} disabled={exportingStock} variant="outline" data-testid="reports-export-stock-on-hand-button">
            {exportingStock ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
            Export Current Stock On Hand (all drawers, for stock taking)
          </Button>
        </div>

        {reports && (
          <div className="space-y-6" data-testid="reports-monthly-view">
            {reports.map((drawer) => (
              <div key={drawer.location_id} className="bg-amtm-surface border border-amtm-border rounded-xl overflow-x-auto">
                <p className="font-semibold text-amtm-text p-3 border-b border-amtm-border">{drawer.location_name}</p>
                {drawer.rows.length === 0 ? (
                  <p className="text-sm text-amtm-textMuted p-4">No activity this month.</p>
                ) : (
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-amtm-textMuted border-b border-amtm-border">
                        <th className="p-2">SR NO</th><th className="p-2">Product</th><th className="p-2">Company</th><th className="p-2">Exp</th><th className="p-2">OP</th><th className="p-2">Days Used</th><th className="p-2">CS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drawer.rows.map((row) => (
                        <tr key={row.item_id} className="border-b border-amtm-border" data-testid={`monthly-report-row-${row.item_id}`}>
                          <td className="p-2">{row.sr_no ?? "—"}</td>
                          <td className="p-2">{row.name}</td>
                          <td className="p-2">{row.company_name || "—"}</td>
                          <td className="p-2">{row.expiry_label || "—"}</td>
                          <td className="p-2">{row.op_stock}</td>
                          <td className="p-2">{Object.entries(row.days).map(([d, q]) => `${d}:${q}`).join(", ") || "—"}</td>
                          <td className="p-2 font-semibold">{row.closing_stock}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
